import { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { DynamicBackground } from "../DynamicBackground";
import { Footer } from "./Footer";
import { ROUTES } from "@/lib/routes";
import { InteractiveFX } from "../fx/InteractiveFX";
import { CursorHalo } from "../fx/CursorHalo";

// Lazy load ChatBot and CookieConsent - not needed for initial render
const ChatBot = lazyWithRetry(() => import("../ChatBot").then(m => ({ default: m.ChatBot })));
const CookieConsent = lazyWithRetry(() => import("../CookieConsent").then(m => ({ default: m.CookieConsent })));

interface LayoutProps {
  children?: React.ReactNode;
}

/**
 * Defer rendering of non-critical UI until the browser is idle.
 * This prevents ChatBot + CookieConsent from blocking the main thread
 * during initial page load, improving TBT and FCP.
 */
function useIdleDefer(delayMs = 2000): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof requestIdleCallback !== "undefined") {
      const id = requestIdleCallback(() => setReady(true), { timeout: delayMs });
      return () => cancelIdleCallback(id);
    }
    // Fallback for Safari
    const t = setTimeout(() => setReady(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);
  return ready;
}

export function Layout({ children }: LayoutProps) {
  const deferReady = useIdleDefer(2500);
  const { pathname } = useLocation();

  return (
    <div className="op-site flex min-h-screen flex-col relative">
      <DynamicBackground />
      <InteractiveFX />
      <CursorHalo />
      <Header />
      <nav
        aria-label="Language discovery links"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}
      >
        {ROUTES.map((route) => (
          <a key={`de-${route.key}`} href={route.de} hrefLang="de">
            German {route.key}
          </a>
        ))}
        {ROUTES.map((route) => (
          <a key={`en-${route.key}`} href={route.en} hrefLang="en">
            English {route.key}
          </a>
        ))}
      </nav>
      <main className="flex-1">
        {/* Keyed by path so every page gently fades and rises in */}
        <div key={pathname} className="op-page-enter">
          <Suspense fallback={<div style={{ minHeight: "80vh" }} />}>
            {children ?? <Outlet />}
          </Suspense>
        </div>
      </main>
      <Footer />
      {deferReady && (
        <Suspense fallback={null}>
          <ChatBot />
          <CookieConsent />
        </Suspense>
      )}
    </div>
  );
}